import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { Contact } from '../../../shared/models/entity/contact/contact.model';
import { ContactFilter } from '../../../shared/models/entity/contact/contact-filter.model';
import { ContactsService } from '../../../shared/services/entity/contacts.service';

const MAX_RESULTS = 6;

/** Searches the Kontakte (Eigentümer, Mieter, Beiräte) by name and email. */
@Injectable({ providedIn: 'root' })
export class ContactsSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(ContactsService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'contacts';

    readonly label = 'Kontakte';

    readonly order = 22;

    readonly icon: Icon = ICON.userCircle;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // The contact list has no entity-scope filters (yet) — a scoped palette skips it
        if (hasUnsupportedScope(scope, [])) {
            return of([]);
        }

        return this.entityService
            .getAll({
                filter: { filter: query } as ContactFilter,
                pagination: { page: 1, pageSize: MAX_RESULTS },
            })
            .pipe(map((items) => items.map((item) => this.toResultItem(item))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(item: Contact): SearchResultItem {
        return {
            id: item.id,
            title: item.name,
            subtitle: [item.email, item.phoneNumber].filter(Boolean).join(' · '),
            icon: this.icon,
            payload: item,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/contacts', item.id]);
                    },
                },
                {
                    id: 'scope',
                    label: 'Als Suchfilter verwenden',
                    icon: ICON.funnel,
                    keepOpen: true,
                    execute: (_, context) => {
                        context.setScope('contactId', item.id, `Kontakt: ${item.name}`);
                    },
                },
            ],
        };
    }
}
